import { postToParent, runSafely, PARENT_EVENTS } from '@/services/parentBridge/parentBridge';
import { buildSlipSnapshot, oddsToRemember } from '@/services/parentBridge/slipSnapshot';

export const SLIP_DEBOUNCE_MS = 100;

/**
 * Vuex plugin of the parent bridge.
 *
 * - SPORT_SLIP_CHANGED after every change to the slip: debounced, and posted only
 *   when the snapshot really changed (the socket updates the cart odds often, and
 *   many cart fields are internal).
 * - SPORT_CART_STATE_CHANGED when the mobile cart opens or closes
 *   (cart.mobileCartOpen, set by every mobile path). The desktop cart keeps its
 *   open state in CartMarketView / CartTerminal, which post it themselves.
 *
 * Registered after vuex-persistedstate, so a slip restored from a previous visit
 * is posted once at start.
 *
 * Nothing here can break the store: installing the plugin, the watchers and the
 * debounced post all run through runSafely.
 *
 * @param {{post?: function, debounceMs?: number}} [options] - injected by the tests
 */
// The odds format the player chose (odds module), decimal when the module is not there
const oddsType = state => (state.odds && state.odds.odds_type) || 'decimal';

export default function createParentBridgePlugin({ post = postToParent, debounceMs = SLIP_DEBOUNCE_MS } = {}) {
    return (store) => runSafely('plugin install', () => {
        let timer = null;
        let lastSlip = null;
        // Odds of the last slip posted, by selectionId: the `from` of a price change that
        // replaces the leg (update_odds) instead of marking it
        let knownOdds = {};

        const flushSlip = () => runSafely('SPORT_SLIP_CHANGED', () => {
            timer = null;
            const snapshot = buildSlipSnapshot(store.state.cart && store.state.cart.bet, knownOdds, oddsType(store.state));
            const key = JSON.stringify(snapshot);
            if (key === lastSlip) return;
            lastSlip = key;
            knownOdds = oddsToRemember(snapshot, knownOdds);
            post(PARENT_EVENTS.SLIP_CHANGED, snapshot);
        });

        const scheduleSlip = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(flushSlip, debounceMs);
        };

        store.watch(state => state.cart && state.cart.bet, scheduleSlip, { deep: true });
        store.watch(
            state => state.cart && state.cart.mobileCartOpen,
            isOpen => runSafely('SPORT_CART_STATE_CHANGED', () => post(PARENT_EVENTS.CART_STATE_CHANGED, { isOpen: isOpen === true }))
        );
        // The player changes the odds format: oddsDisplay changes with it
        store.watch(oddsType, scheduleSlip);

        scheduleSlip();
    });
}
